<script setup lang="ts">
import Navbar from "~/components/layout/Navbar.vue";
import UiButton from "~/components/ui/Button.vue";
import LayoutSection from "~/components/layout/Section.vue";
import Footer from "~/components/layout/Footer.vue";
import Contact from "~/components/layout/Contact.vue";
import config from "~/config.json";
import type {DropdownItem} from "~/components/ui/Dropdown.vue";

useHead({
  title: "Ochrana osobních údajů",
});

const servicesDropdownItems: DropdownItem[] = config["services"].flatMap((service) => {
  return service["services-list"].map((item) => ({
    label: item.title,
    value: item.title
  }));
});
</script>

<template>
  <LayoutSection id="hero" background="/images/hero-background.jpg" parallax>
    <Navbar />

    <div class="content">
      <h1>Ochrana osobních údajů</h1>
      <p>Seznamte se s pravidly ochrany osobních údajů na našich stránkách. Zjistěte, jak jsou osobní údaje shromažďovány, zpracovávány a chráněny.</p>

      <div class="buttons">
        <UiButton type="primary" variant="solid" href="#contact" trailing-icon="material-symbols:arrow-right-rounded">Kontaktujte nás</UiButton>
        <UiButton type="secondary" variant="solid" href="/">Domů</UiButton>
      </div>
    </div>

    <img src="/images/hero-illustration.svg" title="Ilustrace" class="illustration" aria-label="Ilustrace" fetchpriority="high" loading="eager" provider="static"  alt="Ilustrace"/>
  </LayoutSection>

  <LayoutSection id="privacy-policy">
    <div class="pp-header">
      <h1 class="pp-title">Ochrana osobních údajů</h1>
    </div>

    <div class="pp-section pp-controller">
      <h2 class="pp-heading">1. Správce osobních údajů</h2>

      <p class="pp-text"><strong>Jaroslav Pálka</strong></p>
      <p class="pp-text">Forma podnikání: fyzická osoba podnikající dle živnostenského zákona</p>
      <p class="pp-text">IČO: 653 685 33</p>
      <p class="pp-text">Sídlo / místo podnikání: okres Břeclav</p>
      <p class="pp-text">E-mail: <NuxtLink to="mailto:palkajaro@seznam.cz">palkajaro@seznam.cz</NuxtLink></p>
    </div>

    <div class="pp-section pp-data">
      <h2 class="pp-heading">2. Jaké osobní údaje jsou zpracovávány</h2>

      <p class="pp-text">
        Prostřednictvím kontaktního nebo objednávkového formuláře mohou být zpracovávány následující osobní údaje:
      </p>

      <ul class="pp-list">
        <li>jméno a příjmení</li>
        <li>e-mailová adresa</li>
        <li>typ služby (pokud je uvedeno)</li>
        <li>telefonní číslo (pokud je uvedeno)</li>
        <li>obsah zprávy nebo poptávky</li>
        <li>technické údaje (např. IP adresa)</li>
      </ul>

      <p class="pp-text">
        Osobní údaje jsou poskytovány dobrovolně subjektem údajů.
      </p>
    </div>

    <div class="pp-section pp-purpose">
      <h2 class="pp-heading">3. Účel zpracování osobních údajů</h2>

      <p class="pp-text">Osobní údaje jsou zpracovávány za účelem:</p>

      <ul class="pp-list">
        <li>vyřízení dotazu nebo objednávky služby</li>
        <li>následné komunikace související s poptávkou</li>
      </ul>

      <p class="pp-text">
        Osobní údaje nejsou využívány k marketingovým účelům ani k zasílání obchodních sdělení.
      </p>
    </div>

    <div class="pp-section pp-legal">
      <h2 class="pp-heading">4. Právní základ zpracování osobních údajů</h2>

      <p class="pp-text">Zpracování osobních údajů probíhá na základě:</p>

      <ul class="pp-list">
        <li>
          plnění smlouvy nebo provedení opatření před uzavřením smlouvy dle čl. 6 odst. 1 písm. b) GDPR
        </li>
        <li>
          oprávněného zájmu správce dle čl. 6 odst. 1 písm. f) GDPR,
          spočívajícího ve vyřízení poptávky a komunikaci se zájemcem o službu
        </li>
      </ul>
    </div>

    <div class="pp-section pp-retention">
      <h2 class="pp-heading">5. Doba uchovávání osobních údajů</h2>

      <p class="pp-text">
        Osobní údaje jsou uchovávány po dobu nezbytně nutnou k vyřízení poptávky
        nebo objednávky služby.
      </p>

      <p class="pp-text">
        Po jejím vyřízení jsou osobní údaje uchovávány pouze po dobu nezbytnou
        k ochraně oprávněných zájmů správce, nejdéle však po dobu 3 let, pokud nevznikne další spolupráce nebo zákonná povinnost delšího uchování.
      </p>

      <p class="pp-text">
        Osobní údaje jsou uchovávány zejména v e-mailové komunikaci.
      </p>
    </div>

    <div class="pp-section pp-recipients">
      <h2 class="pp-heading">6. Příjemci osobních údajů</h2>

      <p class="pp-text">
        K osobním údajům má přístup správce.
      </p>

      <p class="pp-text">
        Osobní údaje mohou být zpracovávány také poskytovateli technických služeb
        (např. poskytovatel e-mailových nebo hostingových služeb), kteří splňují
        požadavky na ochranu osobních údajů dle GDPR.
      </p>

      <p class="pp-text">
        Osobní údaje nejsou předávány třetím osobám ani mimo Evropskou unii.
      </p>
    </div>

    <div class="pp-section pp-rights">
      <h2 class="pp-heading">7. Práva subjektu údajů</h2>

      <p class="pp-text">Subjekt údajů má v souladu s GDPR právo:</p>

      <ul class="pp-list">
        <li>na přístup ke svým osobním údajům</li>
        <li>na opravu nepřesných nebo neúplných údajů</li>
        <li>na výmaz osobních údajů</li>
        <li>na omezení zpracování</li>
        <li>vznést námitku proti zpracování</li>
        <li>podat stížnost u Úřadu pro ochranu osobních údajů</li>
      </ul>

      <div class="pp-text">
        Uplatnění práv je možné prostřednictvím e-mailu:
        <p class="pp-text">E-mail: <NuxtLink to="mailto:palkajaro@seznam.cz">palkajaro@seznam.cz</NuxtLink></p>
      </div>
    </div>

    <div class="pp-section pp-footer">
      <h2 class="pp-heading">8. Závěrečná ustanovení</h2>

      <p class="pp-text">
        Odesláním kontaktního nebo objednávkového formuláře subjekt údajů potvrzuje,
        že se seznámil s těmito zásadami ochrany osobních údajů.
      </p>

      <p class="pp-text">
        Tyto zásady ochrany osobních údajů jsou platné od <strong>24. 01. 2026</strong>.
      </p>
    </div>
  </LayoutSection>

  <Contact :services-dropdown-items="servicesDropdownItems" />
  <Footer />
</template>

<style scoped lang="scss">
#hero {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 80px;
  min-height: 550px;
  position: relative;
  z-index: 1;

  .illustration {
    position: absolute;
    width: 20%;
    bottom: 0;
    right: 3%;
    transform: translateY(11%);
    pointer-events: none;
    user-select: none;
  }

  .content {
    display: flex;
    flex-direction: column;
    max-width: 800px;
    gap: 30px;
    z-index: 1;

    h1 {
      font-size: 51px;
      font-weight: 700;
      color: rgb(var(--title-dark-color));
    }

    p {
      font-size: 18px;
      color: rgb(var(--text-dark-color));
    }

    .buttons {
      display: flex;
      gap: 20px;
      flex-wrap: wrap;
    }
  }
}

#privacy-policy {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: rgba(var(--text-light-color));

  .pp-section {
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
    margin-bottom: 30px;
  }

  .pp-heading {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    font-weight: 700;
    color: rgb(var(--title-light-color));
  }

  b, strong {
    color: rgba(var(--mini-title-light-color));
  }

  a {
    color: rgb(var(--btn-primary-background));
    text-decoration: none;
    transition: 0.2s;

    &:hover {
      color: rgb(var(--btn-primary-hover-background));
    }
  }

  .pp-text {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 6px;
    font-size: 18px;
  }

  .pp-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding-left: 20px;
    font-size: 18px;
  }

  .pp-consent {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 6px;
    font-size: 18px;
  }

  .pp-header {
    display: flex;
    width: 100%;
    margin-bottom: 40px;
    font-weight: 700;
    color: rgb(var(--title-light-color));
  }

  .pp-title {
    font-weight: bold;
    font-weight: 700;
    color: rgb(var(--title-light-color));
  }
}

@media (max-width: 700px) {
  #hero .illustration {
    display: none;
  }
}
</style>